/* eslint-disable camelcase */
import { faSpinner } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Grid, TextField } from '@mui/material';
import type { GridSize } from '@mui/material';
import { SelectCountry } from 'components';
import { AdornmentLoader } from 'components/Form/Adornment/Adornment';
import { useStores } from 'hooks';
import { CountryView } from 'models';
import { getIdentifier, getIdentifiers } from 'models/detail/CountryFormView';
import { IdentifierView, RuleView } from 'models/detail/IdentifierView';
import { useSnackbar } from 'notistack';
import React, {
  useCallback, useEffect, useState
} from 'react';
import {
 Control, Controller, UseFormWatch, UseFormSetValue, UseFormGetValues, UseFormClearErrors
} from 'react-hook-form';
import { CompanyService, CountriesService } from 'services';
import { i18nStore } from 'stores';
import { STRUCTURE_TYPE } from 'utils/constants';
import { InterventionHelper, translate, UserHelper } from 'utils/helpers';
import { checkRegexMismatch } from 'utils/helpers/FormHelper';
import { CustomTooltip } from '../../Tooltips/Tooltips';
import FormElement from '../FormElement/FormElement';

interface FormCountryIdentifierProps {
  clearErrors?: UseFormClearErrors<any>;
  columnSize?: GridSize;
  // React Hook Form props
  control: Control<any>;

  getValues: UseFormGetValues<any>;
  hasDefaultSiret?: string;
  identifierFields: IdentifierView[];
  setIdentifierFields: (identifierFields: IdentifierView[]) => void;
  setValue: UseFormSetValue<any>;

  watch: UseFormWatch<any>;
}

const FormCountryIdentifier = ({
                                 columnSize = 6,
                                 identifierFields,
                                 setIdentifierFields,
                                 control,
                                 watch,
                                 setValue,
                                 getValues,
                                 clearErrors,
                                 hasDefaultSiret
                               }: FormCountryIdentifierProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { currentLanguage } = i18nStore;
  const { userStore } = useStores();
  const [isSearchingSiret, setIsSearchingSiret] = useState(false);
  const [isFetchingCountries, setIsFetchingCountries] = useState(false);
  const [countriesDetails, setCountriesDetails] = useState<CountryView[]>([]);
  const [institutionIdentifier, setInstitutionIdentifier] = useState(null);
  const [institutionIdentifierRule, setInstitutionIdentifierRule] = useState<RuleView>(null);

  // Watch form values
  const countryValue = watch('country');
  const isPublic = watch('isPublic');

  const setCountries = useCallback((countries: CountryView[]) => {
    setCountriesDetails(countries);
    const countriesOptions = InterventionHelper.MAP_COUNTRIES(countries);
    const currentCountry = getValues('country');
    if (!currentCountry) {
      const defaultCountry = UserHelper.getDefaultCountry();
      const defaultCountryOption = countriesOptions.find(country => country.value === defaultCountry.id);
      if (defaultCountryOption) {
        setValue('country', defaultCountryOption, { shouldValidate: false });
      }
    }
  }, [setValue, getValues]);

  useEffect(() => {
    setIsFetchingCountries(true);
    if (userStore.isOffline) {
      InterventionHelper.loadCountriesStored()
          .then(setCountries)
          .finally(() => setIsFetchingCountries(false));
    } else {
      CountriesService.getCountryDetails()
          .then(setCountries)
          .finally(() => setIsFetchingCountries(false));
    }
  }, [userStore.isOffline, setCountries]);

  const initValueIdentifierField = useCallback((identifierField: IdentifierView) => {
    const currentValues = getValues();
    const defaultIdentifier = getIdentifiers(currentValues).find(identifier => identifier.idField === identifierField.idField);
    const value = identifierField.value ? identifierField.value : ((identifierField.primary && getIdentifier(currentValues)) || '');

    return defaultIdentifier ? {
      ...identifierField,
      value: defaultIdentifier.value || value,
      idValue: defaultIdentifier.idValue,
      suffix: defaultIdentifier.suffix
    } : {
      ...identifierField,
      value
    };
  }, [getValues]);

  const initValueIdentifierFields = useCallback((identifierFieldsToInit: IdentifierView[]) => {
    const currentValues = getValues();
    if (getIdentifiers(currentValues)) {
      const initializedIdentifierValues = identifierFieldsToInit.map(identifierField => initValueIdentifierField(identifierField));
      setIdentifierFields(initializedIdentifierValues);
    } else {
      setIdentifierFields(identifierFieldsToInit);
    }
  }, [getValues, initValueIdentifierField, setIdentifierFields]);

  const updateIdentifierFields = useCallback(() => {
    const currentValues = getValues();
    if (!currentValues?.country) {
      initValueIdentifierFields([]);
      setInstitutionIdentifierRule(null);
      return;
    }

    const currentIdentifierFields = countriesDetails.find(country => country.id.toString() === currentValues.country.value.toString());
    if (!currentIdentifierFields || !currentIdentifierFields.identifiers) {
      initValueIdentifierFields([]);
      setInstitutionIdentifierRule(null);
      return;
    }

    const identifiers = currentIdentifierFields.identifiers.filter(identifier => identifier.type === STRUCTURE_TYPE.INSTITUTION_MODEL);
    initValueIdentifierFields(identifiers);
    setInstitutionIdentifierRule(identifiers.find(identifier => identifier.primary)?.rule);
  }, [getValues, initValueIdentifierFields, countriesDetails]);

  const handleIdentifierValueChange = useCallback((identifierField: IdentifierView) => {
    if (identifierField.primary) {
      setInstitutionIdentifier(identifierField.value);
    }
    setIdentifierFields(identifierFields.map(identifier => (identifier.idField === identifierField.idField ? identifierField : identifier)));
  }, [identifierFields, setIdentifierFields]);

  const handleChangeSelectedCountry = useCallback((countryItem: any) => {
    const currentValues = getValues();
    setValue('address', {
      ...currentValues?.address,
      country: countryItem
    });
    setValue('country', countryItem);
  }, [getValues, setValue]);

  useEffect(() => {
    updateIdentifierFields();
  }, [countryValue, updateIdentifierFields]);

  const resetInstitutionFields = useCallback(() => {
    setValue('institutionName', '');
    setValue('address1', '');
    setValue('postalCode', '');
    setValue('city', '');
    setValue('address2', '');

    if (clearErrors) {
      clearErrors(['institutionName', 'address1', 'postalCode', 'city', 'address2']);
    }
  }, [setValue, clearErrors]);

  const handleIdentifierSuffixChange = useCallback((identifierField: IdentifierView) => {
    if (identifierField.primary) {
      setIdentifierFields(identifierFields.map(identifier => (identifier.idField === identifierField.idField ? identifierField : identifier)));
    }
  }, [identifierFields, setIdentifierFields]);

  const getInstitutionBySiret = useCallback(() => {
    setIsSearchingSiret(true);
    CompanyService.getInstitutionBySiret(institutionIdentifier)
        .then(response => {
          if (response.header && response.header.statut === 200) {
            resetInstitutionFields();
            const tempInstitution = response.etablissement.uniteLegale.denominationUniteLegale;
            const tempAddress = response.etablissement.adresseEtablissement.codePostalEtablissement;

            setValue('institutionName', tempInstitution);

            if (tempAddress) {
              const address1 = `${response.etablissement.adresseEtablissement.numeroVoieEtablissement || ''} ${response.etablissement.adresseEtablissement.typeVoieEtablissement || ''} ${response.etablissement.adresseEtablissement.libelleVoieEtablissement || ''}`;
              const address2 = response.etablissement.adresseEtablissement.complementAdresseEtablissement || '';
              const city = response.etablissement.adresseEtablissement.libelleCommuneEtablissement || '';

              setValue('address1', address1);
              setValue('address2', address2);
              setValue('postalCode', tempAddress);
              setValue('city', city);
            }
          } else {
            enqueueSnackbar(response.header.message, { variant: 'warning', autoHideDuration: 5000 });
          }
        })
        .catch(() => enqueueSnackbar(translate('errors.noMatchingSiret'), { variant: 'warning' }))
        .finally(() => setIsSearchingSiret(false));
  }, [institutionIdentifier, setValue, resetInstitutionFields, enqueueSnackbar]);

  useEffect(() => {
    // Launch siret search only if the siret is complete and the country is France
    const currentValues = getValues();
    if (institutionIdentifier
        && institutionIdentifier.match(`^${institutionIdentifierRule?.format}$`)
        && (
            currentValues?.country
            && currentValues.country.countryCode === 'fr'
        )
    ) {
      getInstitutionBySiret();
    }
  }, [institutionIdentifier, institutionIdentifierRule, getValues, getInstitutionBySiret]);

  return (
    <>
      <FormElement label={translate('common.country')}>
        <SelectCountry
              required
              value={countryValue || null}
              onChange={handleChangeSelectedCountry}
          />
      </FormElement>

      <FormElement>
        <Grid container>
          {isFetchingCountries
                ? (
                  <Grid item sx={{ paddingLeft: '10px' }}>
                    <FontAwesomeIcon icon={faSpinner} spin />
                    {translate('common.loading')}
                  </Grid>
                ) : (
                    identifierFields.filter(identifier => identifier.type === STRUCTURE_TYPE.INSTITUTION_MODEL)
                        .map(identifierField => {
                          const currentValues = getValues();
                          const currentCountry = countriesDetails.find(country => country.id.toString() === currentValues?.country?.value?.toString());
                          const freshIdentifier = currentCountry?.identifiers?.find(id => id.idField === identifierField.idField);
                          const rule = freshIdentifier?.rule;
                          let correctedRuleFormat = rule?.format;
                          if (correctedRuleFormat && !correctedRuleFormat.startsWith('^')) {
                            correctedRuleFormat = `^${correctedRuleFormat}$`;
                          }

                          return (
                            <Grid container item key={identifierField.idField}>
                              <Grid item sm={columnSize} xs={12}>
                                <TextField
                                      error={
                                          identifierField?.value
                                          && identifierField.value.length > 0
                                          && correctedRuleFormat
                                          && !new RegExp(correctedRuleFormat).test(identifierField?.value)
                                      }
                                      helperText={(rule?.format && rule?.translations?.find(val => val.code === currentLanguage)?.label) ?? ''}
                                      inputProps={correctedRuleFormat ? { pattern: correctedRuleFormat } : {}}
                                      label={identifierField.name}
                                      name={identifierField.name}
                                      required={identifierField.required}
                                      value={identifierField.value ?? ''}
                                      onChange={value => {
                                        const newValue = value.target.value;
                                        handleIdentifierValueChange({
                                          ...identifierField,
                                          value: newValue
                                        });
                                      }}
                                  />
                              </Grid>
                              {isPublic && identifierField.primary && (
                                <Grid item sm={columnSize} xs={12}>
                                  <Grid container>
                                    <TextField
                                            label={translate('common.identifierSuffix')}
                                            name="identifierSuffix"
                                            required
                                            value={identifierField.suffix ?? ''}
                                            onChange={value => handleIdentifierSuffixChange({
                                              ...identifierField,
                                              suffix: value.target.value
                                            })}
                                        />
                                    <CustomTooltip text={translate('common.identifierSuffixTooltip')} />
                                  </Grid>
                                </Grid>
                                )}
                            </Grid>
                          );
                        })
                )
            }
          <Grid item sm={columnSize} xs={12}>
            <Controller
                  control={control}
                  name="institutionName"
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                          {...field}
                          autoComplete="new-password"
                          error={!!error}
                          helperText={error?.message}
                          InputProps={{
                            endAdornment: (isSearchingSiret && <AdornmentLoader />)
                          }}
                          label={translate('common.institutionName')}
                          required
                      />
                  )}
                  rules={{ required: translate('errors.required') }}
              />
          </Grid>
        </Grid>
        {/* This input is only used for forcing disabling autocomplete in Chrome browser */}
        <input
              name="fakeInputForForcingDisablingAutocompleteChrome3"
              style={{ display: 'none' }}
              type="text"
          />
      </FormElement>
    </>
  );
};

export default FormCountryIdentifier;
